import { useRef, forwardRef, useEffect, useState } from 'react'
import styled from 'styled-components'

import { Flex, Text } from 'components'
import { TextProps } from 'components/Text'

type Props = TextProps

// part of v2 revamp
const TextWithEllipsedCount = ({ children, ...props }: Props) => {
  const isString = typeof children === 'string'

  const ele = useRef<HTMLDivElement>(null)
  const [visibleText, setVisibleText] = useState('')
  const [moreCount, setMoreCount] = useState(0)

  // Only run effects when it's a string
  useEffect(() => {
    if (!isString) return
    if (!ele.current) return

    const update = () => {
      if (ele.current) {
        const result = getClampedText(ele.current)
        setVisibleText(result)
      }
    }

    update()

    const ro = new ResizeObserver(update)
    ro.observe(ele.current)

    window.addEventListener('resize', update)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [isString])

  useEffect(() => {
    if (!isString) return
    if (!visibleText) return

    const visibleAssignees = visibleText.split(',')
    const actualAssignees = children.split(',')

    setMoreCount(actualAssignees.length - visibleAssignees.length)
  }, [visibleText, children, isString])

  if (!isString) {
    return null
  }

  return (
    <Flex width={'100%'} gap="5px">
      <ForwardedText ref={ele} {...props}>
        {children}
      </ForwardedText>
      {moreCount !== 0 && (
        <div style={{ flexShrink: 0, marginTop: 'auto' }}>
          <Text color={props?.color}> + {moreCount}</Text>
        </div>
      )}
    </Flex>
  )
}

const ForwardedText = forwardRef<HTMLDivElement, TextProps>((props, ref) => (
  <div ref={ref}>
    <Text {...props} />
  </div>
))

export const TaskTitleText = styled(Text)`
  font-size: 14px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: normal;
`
export default TextWithEllipsedCount

// utils
function getClampedText(wrapper: HTMLDivElement): string {
  const textEl = wrapper.firstElementChild as HTMLElement | null
  if (!textEl) return ''

  const textNode = textEl.firstChild as Text | null
  if (!textNode) return ''

  const fullText = textNode.textContent ?? ''
  if (!fullText) return ''

  const lineClamp = getComputedStyle(textEl).webkitLineClamp
  const maxLines = Number(lineClamp) || 1

  const lineHeight = parseFloat(getComputedStyle(textEl).lineHeight)
  const maxHeight = lineHeight * maxLines

  if (textEl.scrollHeight <= maxHeight + 1) {
    return fullText
  }

  let low = 0
  let high = fullText.length - 1
  let result = ''

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    textNode.textContent = fullText.slice(0, mid) + '…'

    if (textEl.scrollHeight > maxHeight + 1) {
      high = mid - 1
    } else {
      result = textNode.textContent
      low = mid + 1
    }
  }

  textNode.textContent = fullText

  return result || fullText
}
